import { Icon, IconButton } from '@ui'
import { seatable } from '@ui/canvas/utils'
import { countBy, get, union, uniqBy } from 'lodash'
import React, { FC, useState } from 'react'
import css from './multiSelectSpaceTab.module.scss'
import { FloorMapSpaces } from '../../api/types'
import { pluralize } from '@utils'
import cx from 'classnames'
import { Tooltip2 } from '@blueprintjs/popover2'
import { Tag } from '@runeffective/evo-design-system'
import Accordion from './Accordion'
import { useFloor } from '@providers/mapProvider'

import AllocationSection from 'components/AllocationSection'
import SpaceTypesSection from 'components/SpaceTypesSection'
import OccupancySection from 'components/OccupancySection'
import RoleSection from 'components/RoleSection'
import AreaSection from 'components/AreaSection'
import CapacitySection from './CapacitySection'
import useSettings from 'components/NewSettings/hooks/useSettings'
import { QRCodeDialog } from './QRCodeDialog'

type MultiSelectionProps = {
  spaces: FloorMapSpaces
  selectedItems: string[]
}

type MultiSelectSpaceTabProps = {
  setSplitScreen: (value: boolean) => void
  spaces: FloorMapSpaces
  selectedItems: string[]
}

const MultiSelectSpaceTab: FC<MultiSelectSpaceTabProps> = ({
  setSplitScreen,
  spaces,
  selectedItems,
}) => {
  const floor = useFloor()
  return (
    <>
      <div className={css.spaceDetails}>
        <IconButton
          className={css.closeButton}
          round={false}
          minimal
          iconName="close"
          onClick={() => {
            floor.selection?.clear()
            setSplitScreen(false)
          }}
        />
        <MultiSelectionCounters spaces={spaces} selectedItems={selectedItems} />
        <MultiSelectionDeskUsageType spaces={spaces} selectedItems={selectedItems} />
        <MultiSelectionEmployees spaces={spaces} selectedItems={selectedItems} />
        <MultiSelectionSpaceCode spaces={spaces} selectedItems={selectedItems} />
        <MultiSelectionAllocations spaces={spaces} selectedItems={selectedItems} />
        <MultiSelectionSpaceTypes spaces={spaces} selectedItems={selectedItems} />
        <MultiSelectionAssignedArea spaces={spaces} selectedItems={selectedItems} />
        <MultiSelectionCapacity spaces={spaces} selectedItems={selectedItems} />
        <MultiSelectionOccupancy spaces={spaces} selectedItems={selectedItems} />
        <MultiSelectionEmployeeRole spaces={spaces} selectedItems={selectedItems} />
      </div>
    </>
  )
}

const MultiSelectionCounters: FC<MultiSelectionProps> = ({ spaces, selectedItems }) => {
  const { empCount, seatCount, spaceCount } = count(spaces, selectedItems)
  const marginRight = 10

  const spaceCountEl = spaceCount ? (
    <span style={{ marginRight }}>
      {spaceCount} {pluralize(spaceCount, 'space')}
      {seatCount ? ',' : ''}
    </span>
  ) : null

  const seatCountEl = seatCount ? (
    <span style={{ marginRight }}>
      {seatCount} {pluralize(seatCount, 'seat')}
      {empCount ? ',' : ''}
    </span>
  ) : null

  const empCountEl = empCount ? (
    <span style={{ marginRight }}>
      {empCount} {pluralize(empCount, 'employee')}
    </span>
  ) : null

  return (
    <div className={css.count}>
      {spaceCountEl}
      {seatCountEl}
      {empCountEl}
    </div>
  )
}

const MultiSelectionEmployees: FC<MultiSelectionProps> = ({ spaces, selectedItems }) => {
  const [active, setActive] = useState(true)
  const spaceEmployeesList = selectedItems
    .map((item) => spaces?.byId[item]?.spaceAssignment?.employee?.name)
    .filter((name) => name)

  const sharedSpaceEmployeesList = selectedItems
    .flatMap((item) => spaces?.byId[item]?.spaceAssignment?.sharedEmployees)
    .filter((sharedEmployee) => sharedEmployee)
    .map((employee) => employee?.name)
  const allEmployeesList = union([...spaceEmployeesList, ...sharedSpaceEmployeesList])

  const toolbar = (
    <div className={css.toolbar}>
      <Icon
        light
        name={active ? 'chevron-down' : 'angle-right'}
        className={cx('fa-light', css.angleIcon)}
      />
      <span>Selected employees</span>
      <span className={css.toolbarTag}>
        <Tag title={allEmployeesList.length.toString()} variant="Informational"></Tag>
      </span>
    </div>
  )
  const content = (
    <div className={css.multiSelectSpace}>
      {allEmployeesList.map((emp) => (
        <span className={css.tag}>
          <Tag title={emp ? emp : ''} />
        </span>
      ))}
    </div>
  )

  const data = { toolbar: toolbar, content: content }
  return spaceEmployeesList.length ? (
    <div className={css.empList}>
      <Accordion data={[data]} selIndx={0} onChange={(_i, isOpen) => setActive(isOpen)} />
    </div>
  ) : null
}

const MultiSelectionDeskUsageType: FC<MultiSelectionProps> = ({ spaces, selectedItems }) => {
  const [active, setActive] = useState(true)
  const floor = useFloor()
  const settings = useSettings()
  const [isQRCodePopoverOpen, setIsQRCodePopoverOpen] = useState(false)
  const deskUsageTypeList = selectedItems?.map((id) => {
    return {
      id,
      space: spaces?.byId[id]?.usageType?.displayName,
      usageTypeName: spaces?.byId[id]?.usageType?.name,
    }
  })
  const deskUsageTypeWithCount = countBy(deskUsageTypeList, (usageType) => usageType.space)

  const sortedList = Object.keys(deskUsageTypeWithCount)
    .map((usageType) => {
      return {
        usageType,
        usageTypeName: uniqBy(
          deskUsageTypeList
            .filter(({ space }) => space === usageType)
            .map((space) => space.usageTypeName),
          usageType
        )?.[0],
        count: deskUsageTypeWithCount[usageType],
        ids: deskUsageTypeList.filter(({ space }) => space === usageType).map((space) => space?.id),
      }
    })
    .filter((space) => space.usageType !== 'undefined')

  const printableQrCodeSpaces = sortedList.filter((space) =>
    floor.printableQrCodeUsageTypes.includes(space?.usageTypeName as string)
  )

  const nonPrintableQrCodeSpaces = settings.qrCodeEnabled
    ? sortedList.filter(
        (space) => !floor.printableQrCodeUsageTypes.includes(space?.usageTypeName as string)
      )
    : sortedList

  selectedItems = selectedItems?.filter((id) =>
    printableQrCodeSpaces?.flatMap((space) => space.ids).includes(id)
  )

  const toolbar = (
    <div className={css.toolbar}>
      <Icon
        light
        name={active ? 'chevron-down' : 'angle-right'}
        className={cx('fa-light', css.angleIcon)}
      />
      <span>Desk usage types</span>
      <span className={css.toolbarTag}>
        <Tag title={sortedList.length.toString()} variant="Informational"></Tag>
      </span>
    </div>
  )

  const content =
    settings.qrCodeEnabled && printableQrCodeSpaces.length ? (
      <>
        <div className={css.multiSelectSpaceWrapper}>
          <div className={css.multiSelectSpace}>
            {printableQrCodeSpaces.map(({ ids: spaceIds, usageType, count }, index) => (
              <span className={css.tag} key={index}>
                <Tag
                  isRemovable
                  onRemove={() => floor.selection?.remove(spaceIds)}
                  title={usageType ? `${count > 1 ? count : ''} ${usageType}` : ''}
                />
              </span>
            ))}
          </div>
          <div>
            <span onClick={() => setIsQRCodePopoverOpen(true)}>
              <Tooltip2 content="Download QR code">
                <Icon light name="qrcode" className={cx('fa-light', css.QRCodeIcons)} />
              </Tooltip2>
            </span>
          </div>
        </div>
        {!!nonPrintableQrCodeSpaces.length && (
          <div className={`${css.multiSelectSpaceWrapper} ${css.divBorder}`}>
            <div className={css.multiSelectSpace}>
              {nonPrintableQrCodeSpaces.map(({ ids: spaceIds, usageType, count }, index) => (
                <span className={css.tag} key={index}>
                  <Tag
                    isRemovable
                    onRemove={() => floor.selection?.remove(spaceIds)}
                    title={usageType ? `${count > 1 ? count : ''} ${usageType}` : ''}
                  />
                </span>
              ))}
            </div>
          </div>
        )}
      </>
    ) : (
      <div className={css.multiSelectSpaceWrapper}>
        <div className={css.multiSelectSpace}>
          {nonPrintableQrCodeSpaces.map(({ ids: spaceIds, usageType, count }, index) => (
            <span className={css.tag} key={index}>
              <Tag
                isRemovable
                onRemove={() => floor.selection?.remove(spaceIds)}
                title={usageType ? `${count > 1 ? count : ''} ${usageType}` : ''}
              />
            </span>
          ))}
        </div>
      </div>
    )

  const data = { toolbar: toolbar, content: content }
  return (
    <>
      <div className={css.deskUsageType}>
        <Accordion data={[data]} selIndx={0} onChange={(_i, isOpen) => setActive(isOpen)} />
      </div>
      {selectedItems && (
        <QRCodeDialog
          isQRCodePopoverOpen={isQRCodePopoverOpen}
          setIsQRCodePopoverOpen={setIsQRCodePopoverOpen}
          spaceIds={selectedItems}
        />
      )}
    </>
  )
}

const MultiSelectionSpaceCode: FC<MultiSelectionProps> = ({ spaces, selectedItems }) => {
  const [active, setActive] = useState(true)
  const floor = useFloor()
  const spaceCodeList = selectedItems?.map((id) => {
    return { id, code: spaces?.byId[id]?.code }
  })

  const toolbar = (
    <div className={css.toolbar}>
      <Icon
        light
        name={active ? 'chevron-down' : 'angle-right'}
        className={cx('fa-light', css.angleIcon)}
      />
      <span>Selected spaces</span>
      <span className={css.toolbarTag}>
        <Tag title={spaceCodeList.length.toString()} variant="Informational"></Tag>
      </span>
    </div>
  )
  const content = (
    <div className={css.multiSelectSpaceWrapper}>
      <div className={css.multiSelectSpace}>
        {spaceCodeList.map(({ id: spaceId, code }) => (
          <span className={css.tag}>
            <Tag
              key={spaceId}
              isRemovable
              onRemove={() => floor.selection?.remove([spaceId])}
              title={code}
            />
          </span>
        ))}
      </div>
      <div>
        <Tooltip2 content="Share space">
          <Icon light name="share-nodes" className={cx('fa-light', css.shareIcon)} />
        </Tooltip2>
      </div>
    </div>
  )

  const data = { toolbar: toolbar, content: content }
  return (
    <div className={css.spaceCode}>
      <Accordion data={[data]} selIndx={0} onChange={(_i, isOpen) => setActive(isOpen)} />
    </div>
  )
}

const MultiSelectionAllocations: FC<MultiSelectionProps> = ({ spaces, selectedItems }) => {
  const [active, setActive] = useState(true)
  const spaceEmployeesList = selectedItems
    .map((item) => spaces?.byId[item]?.spaceAssignment?.employee?.id)
    .filter((id) => id)

  const sharedSpaceEmployeesList = selectedItems
    .flatMap((item) => spaces?.byId[item]?.spaceAssignment?.sharedEmployees)
    .filter((sharedEmployee) => sharedEmployee)
    .map((employee) => employee?.id)
  const allEmployeesList = union([...spaceEmployeesList, ...sharedSpaceEmployeesList]) as string[]

  const toolbar = (
    <div className={css.toolbar}>
      <Icon
        light
        name={active ? 'chevron-down' : 'angle-right'}
        className={cx('fa-light', css.angleIcon)}
      />
      <span>Department allocations</span>
    </div>
  )
  const content = (
    <AllocationSection
      spaces={spaces}
      selectedItems={selectedItems}
      allEmployeesList={allEmployeesList}
    />
  )
  const data = { toolbar: toolbar, content: content }
  return (
    <div className={css.spaceCode}>
      <Accordion data={[data]} selIndx={0} onChange={(_, isOpen) => setActive(isOpen)} />
    </div>
  )
}
const MultiSelectionSpaceTypes: FC<MultiSelectionProps> = ({ spaces, selectedItems }) => {
  const [active, setActive] = useState(true)

  const toolbar = (
    <div className={css.toolbar}>
      <Icon
        light
        name={active ? 'chevron-down' : 'angle-right'}
        className={cx('fa-light', css.angleIcon)}
      />
      <span>Space types</span>
    </div>
  )
  const content = <SpaceTypesSection spaces={spaces} selectedItems={selectedItems} />
  const data = { toolbar: toolbar, content: content }
  return (
    <div className={css.spaceCode}>
      <Accordion data={[data]} selIndx={0} onChange={(_, isOpen) => setActive(isOpen)} />
    </div>
  )
}
const MultiSelectionAssignedArea: FC<MultiSelectionProps> = ({ spaces, selectedItems }) => {
  const [active, setActive] = useState(true)

  const toolbar = (
    <div className={css.toolbar}>
      <Icon
        light
        name={active ? 'chevron-down' : 'angle-right'}
        className={cx('fa-light', css.angleIcon)}
      />
      <span>Assigned area</span>
    </div>
  )
  const content = <AreaSection spaces={spaces} selectedItems={selectedItems} />
  const data = { toolbar: toolbar, content: content }
  return (
    <div className={css.spaceCode}>
      <Accordion data={[data]} selIndx={0} onChange={(_, isOpen) => setActive(isOpen)} />
    </div>
  )
}
const MultiSelectionCapacity: FC<MultiSelectionProps> = ({ spaces, selectedItems }) => {
  const [active, setActive] = useState(true)

  const toolbar = (
    <div className={css.toolbar}>
      <Icon
        light
        name={active ? 'chevron-down' : 'angle-right'}
        className={cx('fa-light', css.angleIcon)}
      />
      <span>Capacity</span>
    </div>
  )
  const content = <CapacitySection spaces={spaces} selectedItems={selectedItems} />
  const data = { toolbar: toolbar, content: content }
  return (
    <div className={css.spaceCode}>
      <Accordion data={[data]} selIndx={0} onChange={(_, isOpen) => setActive(isOpen)} />
    </div>
  )
}
const MultiSelectionOccupancy: FC<MultiSelectionProps> = ({ spaces, selectedItems }) => {
  const [active, setActive] = useState(true)
  const spaceEmployeesList = selectedItems
    .map((item) => spaces?.byId[item]?.spaceAssignment?.employee?.id)
    .filter((id) => id)

  const sharedSpaceEmployeesList = selectedItems
    .flatMap((item) => spaces?.byId[item]?.spaceAssignment?.sharedEmployees)
    .filter((sharedEmployee) => sharedEmployee)
    .map((employee) => employee?.id)
  const allEmployeesList = union([...spaceEmployeesList, ...sharedSpaceEmployeesList]) as string[]

  const toolbar = (
    <div className={css.toolbar}>
      <Icon
        light
        name={active ? 'chevron-down' : 'angle-right'}
        className={cx('fa-light', css.angleIcon)}
      />
      <span>Occupancy</span>
    </div>
  )
  const content = (
    <OccupancySection
      spaces={spaces}
      selectedItems={selectedItems}
      allEmployeesList={allEmployeesList}
    />
  )
  const data = { toolbar: toolbar, content: content }
  return (
    <div className={css.spaceCode}>
      <Accordion data={[data]} selIndx={0} onChange={(_, isOpen) => setActive(isOpen)} />
    </div>
  )
}
const MultiSelectionEmployeeRole: FC<MultiSelectionProps> = ({ spaces, selectedItems }) => {
  const [active, setActive] = useState(true)
  const spaceEmployeesList = selectedItems
    .map((item) => spaces?.byId[item]?.spaceAssignment?.employee?.id)
    .filter((id) => id)

  const sharedSpaceEmployeesList = selectedItems
    .flatMap((item) => spaces?.byId[item]?.spaceAssignment?.sharedEmployees)
    .filter((sharedEmployee) => sharedEmployee)
    .map((employee) => employee?.id)
  const allEmployeesList = union([...spaceEmployeesList, ...sharedSpaceEmployeesList]) as string[]

  const toolbar = (
    <div className={css.toolbar}>
      <Icon
        light
        name={active ? 'chevron-down' : 'angle-right'}
        className={cx('fa-light', css.angleIcon)}
      />
      <span>Employee role</span>
    </div>
  )
  const content = (
    <RoleSection
      spaces={spaces}
      selectedItems={selectedItems}
      allEmployeesList={allEmployeesList}
    />
  )
  const data = { toolbar: toolbar, content: content }
  return (
    <div className={css.spaceCode}>
      <Accordion data={[data]} selIndx={0} onChange={(_, isOpen) => setActive(isOpen)} />
    </div>
  )
}

const count = (spaces: FloorMapSpaces, selectedItems: string[]) => {
  let empCount = 0
  let seatCount = 0
  let spaceCount = 0
  const { byId } = spaces

  selectedItems?.forEach((id: string) => {
    const obj = byId[id]

    if (obj) {
      if (seatable(obj.spaceType)) {
        seatCount++
        if (get(obj, 'spaceAssignment.employee')) {
          empCount++
        } else if (get(obj, 'spaceAssignment.sharedEmployees')) {
          empCount += obj?.spaceAssignment?.sharedEmployees?.length
            ? obj?.spaceAssignment?.sharedEmployees?.length
            : 0
        }
      } else {
        spaceCount++
      }
    }
  })

  return { empCount, seatCount, spaceCount }
}

export default MultiSelectSpaceTab
